import React, { useCallback, useState } from "react";
import { useTable } from "@refinedev/antd";
import { Table, Input, Space, Tag, Button, Modal, Form, InputNumber, Card, App } from "antd";
import type { ColumnsType } from "antd/es/table";
import { SearchOutlined, PlusOutlined, InboxOutlined, HistoryOutlined, LineChartOutlined } from "@ant-design/icons";
import type { IStockLevel, IStockMovement } from "../../interfaces/stock";
import { stockService } from "../../api/stockService";
import { PageHeader } from "../../components/ui";
import { extractApiError } from "../../utils/apiErrors";

export const StockList: React.FC = () => {
    const { message } = App.useApp();
    const [searchText, setSearchText] = useState<string>("");
    const [adjustModalVisible, setAdjustModalVisible] = useState(false);
    const [historyModalVisible, setHistoryModalVisible] = useState(false);
    const [selectedStock, setSelectedStock] = useState<IStockLevel | null>(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyRows, setHistoryRows] = useState<IStockMovement[]>([]);
    const [adjustSubmitting, setAdjustSubmitting] = useState(false);
    const [form] = Form.useForm();

    const { tableProps, tableQueryResult } = useTable<IStockLevel>({
        resource: "Stock",
    });

    const handleAdjust = (record: IStockLevel) => {
        setSelectedStock(record);
        form.resetFields();
        form.setFieldsValue({ newQuantity: record.currentStockLevel });
        setAdjustModalVisible(true);
    };

    const openHistory = useCallback(
        async (record: IStockLevel) => {
            setSelectedStock(record);
            setHistoryModalVisible(true);
            setHistoryLoading(true);
            setHistoryRows([]);
            try {
                const rows = await stockService.getStockMovements(record.inventoryItemId);
                setHistoryRows(Array.isArray(rows) ? rows : []);
            } catch (e: unknown) {
                message.error(extractApiError(e, "Could not load adjustment history."));
            } finally {
                setHistoryLoading(false);
            }
        },
        [message],
    );

    const handleAdjustSubmit = async (values: { newQuantity: number; reason: string; notes?: string }) => {
        if (!selectedStock) return;

        const qtyChange = Number(values.newQuantity) - Number(selectedStock.currentStockLevel);

        if (qtyChange === 0) {
            message.info("No quantity change detected. Nothing to save.");
            return;
        }

        const adjustment = {
            inventoryItemId: selectedStock.inventoryItemId,
            quantityChange: qtyChange,
            reason: values.reason,
            notes: values.notes,
        };

        setAdjustSubmitting(true);
        try {
            await stockService.adjustStock(adjustment);
            message.success("Stock adjusted.");
            setAdjustModalVisible(false);
            tableQueryResult.refetch();
        } catch (e: unknown) {
            message.error(extractApiError(e, "Failed to adjust stock."));
        } finally {
            setAdjustSubmitting(false);
        }
    };

    const columns: ColumnsType<IStockLevel> = [
        {
            title: "Product Code",
            dataIndex: "productCode",
            key: "productCode",
            render: (value: string) => <span className="font-medium text-text-primary">{value}</span>,
            sorter: true,
        },
        {
            title: "Product Name",
            dataIndex: "productName",
            key: "productName",
            render: (value: string) => <span className="text-text-secondary">{value}</span>,
            sorter: true,
            filteredValue: searchText ? [searchText] : null,
            onFilter: (value, record) =>
                record.productName.toLowerCase().includes(value.toString().toLowerCase()),
        },
        {
            title: "Current",
            dataIndex: "currentStockLevel",
            key: "currentStockLevel",
            render: (value: number) => <span className="font-medium">{value.toFixed(0)}</span>,
            sorter: (a, b) => a.currentStockLevel - b.currentStockLevel,
        },
        {
            title: "Available",
            dataIndex: "availableStock",
            key: "availableStock",
            render: (value: number, record) => {
                const isLow = record.currentStockLevel <= record.reorderPoint;
                return (
                    <span className={isLow ? "font-semibold text-danger" : "text-text-secondary"}>
                        {value.toFixed(0)}
                    </span>
                );
            },
            responsive: ["md"],
        },
        {
            title: "Status",
            key: "status",
            render: (_, record) => {
                if (record.currentStockLevel === 0) {
                    return (
                        <Tag color="error" className="status-tag">
                            Out of Stock
                        </Tag>
                    );
                }
                if (record.currentStockLevel <= record.minimumQuantity) {
                    return (
                        <Tag color="error" className="status-tag">
                            Critical
                        </Tag>
                    );
                }
                if (record.currentStockLevel <= record.reorderPoint) {
                    return (
                        <Tag color="warning" className="status-tag">
                            Low Stock
                        </Tag>
                    );
                }
                return (
                    <Tag color="success" className="status-tag">
                        In Stock
                    </Tag>
                );
            },
        },
        {
            title: "Updated",
            dataIndex: "lastUpdated",
            key: "lastUpdated",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            responsive: ["lg"],
        },
        {
            title: "Actions",
            key: "actions",
            width: 200,
            render: (_, record) => (
                <Space size="small" wrap>
                    <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => handleAdjust(record)}>
                        Adjust
                    </Button>
                    <Button size="small" icon={<HistoryOutlined />} onClick={() => void openHistory(record)}>
                        History
                    </Button>
                </Space>
            ),
        },
    ];

    const historyColumns: ColumnsType<IStockMovement> = [
        { title: "Date", dataIndex: "createdAt", key: "createdAt", render: (v: string) => new Date(v).toLocaleString() },
        {
            title: "Change",
            dataIndex: "quantity",
            key: "quantity",
            render: (v: number) => (
                <span style={{ color: v >= 0 ? "#1677ff" : "#cf1322" }}>
                    {v >= 0 ? "+" : ""}
                    {Number(v).toFixed(2)}
                </span>
            ),
        },
        {
            title: "Balance after",
            dataIndex: "balanceAfter",
            key: "balanceAfter",
            render: (v: number) => Number(v).toFixed(2),
        },
        { title: "Notes", dataIndex: "notes", key: "notes", ellipsis: true },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Stock Levels"
                titleIcon={<InboxOutlined />}
                breadcrumbs={[{ title: "Stock" }]}
                actions={[
                    {
                        label: "Adjustment history",
                        icon: <HistoryOutlined />,
                        href: "/stock/adjustments",
                        type: "default",
                        variant: "outlined",
                    },
                    {
                        label: "Inventory report",
                        icon: <LineChartOutlined />,
                        href: "/reports?tab=inventory",
                        type: "default",
                        variant: "outlined",
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by product name"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        {...tableProps}
                        columns={columns}
                        rowKey="id"
                        pagination={{
                            ...tableProps.pagination,
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 800 }}
                    />
                </Card>
            </div>

            <Modal
                title={`Adjust Stock: ${selectedStock?.productName ?? ""}`}
                open={adjustModalVisible}
                onCancel={() => setAdjustModalVisible(false)}
                onOk={() => form.submit()}
                okText="Adjust Stock"
                confirmLoading={adjustSubmitting}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={(v) => void handleAdjustSubmit(v)}>
                    <Form.Item label="Current Stock">
                        <InputNumber value={selectedStock?.currentStockLevel} disabled style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item
                        label="New Quantity"
                        name="newQuantity"
                        rules={[{ required: true, message: "Please enter the new quantity" }]}
                    >
                        <InputNumber style={{ width: "100%" }} placeholder="Enter the new total quantity" />
                    </Form.Item>
                    <Form.Item label="Reason" name="reason" rules={[{ required: true, message: "Reason is required" }]}>
                        <Input placeholder="e.g., Stock count adjustment" />
                    </Form.Item>
                    <Form.Item label="Notes" name="notes">
                        <Input.TextArea rows={3} placeholder="Additional notes" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`Adjustment history: ${selectedStock?.productName ?? ""}`}
                open={historyModalVisible}
                onCancel={() => setHistoryModalVisible(false)}
                footer={null}
                width={720}
                destroyOnClose
            >
                <Table<IStockMovement>
                    size="small"
                    rowKey="id"
                    loading={historyLoading}
                    dataSource={historyRows}
                    columns={historyColumns}
                    pagination={{ pageSize: 8 }}
                    locale={{ emptyText: "No adjustments recorded yet for this item." }}
                />
            </Modal>
        </div>
    );
};

export default StockList;
